// The stream connection: one websocket to the Go bridge, redialled as needed,
// delivering parsed video packets.
//
// This exists to keep the socket's lifecycle in one place. Previously the socket,
// its reconnect behaviour, the wire parsing, the presence bookkeeping and the UI
// state transitions all lived in a single 90-line message handler, and every fix
// to one risked the others.
import {ReconnectingSocket} from "./reconnecting-socket.js";
import {parseVideoPacket} from "./metadata.js";

export class Transport {
	#socket;
	#closed = false;

	/**
	 * @param {object} options
	 * @param {string} options.url
	 * @param {() => string[]} options.protocols re-read per attempt, because the
	 *   auth token rides in the subprotocol and expires
	 * @param {(packet: import("./metadata.js").VideoPacket) => void} options.onPacket
	 * @param {(text: string) => void} [options.onServerError] the bridge reports
	 *   authorisation failures as a text frame
	 * @param {() => void} [options.onClosed] socket dropped; a redial is pending
	 * @param {() => void} [options.onError]
	 */
	constructor({url, protocols, onPacket, onServerError, onClosed, onError}) {
		this.#socket = new ReconnectingSocket(url, protocols);

		this.#socket.addEventListener("message", (event) => {
			if (this.#closed) return;
			if (typeof event.data === "string") {
				if (onServerError) onServerError(event.data);
				return;
			}
			// binaryType is "arraybuffer", so this is synchronous. With Blob it
			// needed an await per frame, which also made frame ordering depend on
			// promise resolution order.
			let packet;
			try {
				packet = parseVideoPacket(event.data);
			} catch (e) {
				// A malformed packet must not kill the stream: the handler used to be
				// async, so a throw became an unhandled rejection and the canvas went
				// black permanently.
				console.error("could not parse a video packet", e);
				return;
			}
			if (packet) onPacket(packet);
		});

		this.#socket.addEventListener("close", () => {
			if (this.#closed) return;
			if (onClosed) onClosed();
		});

		this.#socket.addEventListener("error", () => {
			if (this.#closed) return;
			if (onError) onError();
		});
	}

	/** Bytes already queued on the socket; used to avoid piling up input. */
	get bufferedAmount() {
		return this.#socket.bufferedAmount;
	}

	/**
	 * @param {ArrayBufferView} bytes
	 * @returns {boolean} false when the socket is not open, in which case the
	 *   message is DROPPED rather than queued — input is a full-state snapshot, so a
	 *   stale one is worse than none.
	 */
	send(bytes) {
		if (this.#closed) return false;
		return this.#socket.send(bytes);
	}

	close() {
		this.#closed = true;
		this.#socket.close();
	}
}
